import store from '@/store'
import config from '@/config'

/**
 * Event object definition for use by Killswitch
 * @typedef {Object} KillswitchEvent
 * @property {number} idfoevent
 * @property {string} idfosporttype
 * @property {string} idfosport
 * @property {Array<KillswitchMarket>} markets
 * @property {boolean} istradable
 * @property {boolean} islive
 */
/**
 * Market object definition for use by Killswitch
 * @typedef {Object} KillswitchMarket
 * @property {number} idfomarket
 * @property {number} idfomarkettype
 * @property {string} big3markettype
 * @property {string} idfosporttype
 * @property {string} idfosport (present if copied from event in some generator calls)
 * @property {string} csvpricetypes
 * @property {Array<KillswitchSelection>} selections
 * @property {boolean} istradable
 */
/**
 * Selection object definition for use by Killswitch
 * @typedef {Object} KillswitchSelection
 * @property {number} idfoselection
 * @property {number} idfomarket (present if selection is prepared for betslip)
 * @property {number} idfoevent (present if selection is prepared for betslip)
 * @property {string} idfosporttype (present if selection is prepared for betslip)
 * @property {string} idfosport (present if selection is prepared for betslip)
 * @property {string} csvpricetypes (present if selection is prepared for betslip)
 * @property {string} idfobolifestate - present, but do we need it? 'NR' will stop adding for this to BetSlip
 * @property {boolean} istradable
 */

export default {
  data: () => ({
  }),

  computed: {
    ksIsKillswitchActive () { return config.app.autoconf.KILLSWITCH_PERIODIC_REFRESH_INTERVAL >= 30 },
    ksSuspensions () { return this.ksIsKillswitchActive ? store.getters['killswitchState/getSuspensions'] : [] },
    ksLiveEvents () { return store.getters['livebettingState/liveEvents'] || [] }
  },

  methods: {
    ksKillswitchInit () { store.dispatch('killswitchState/fetchSuspensions') },
    /**
     * Convert price types csv to array
     * @param {string} price types in CSV format
     * @returns {Array}
     */
    ksPriceTypeCsvToArray (csv) {
      let arr = []
      if (typeof csv !== 'undefined' && csv !== null) {
        if (Array.isArray(csv)) {
          csv.forEach((v) => {
            if (typeof v === 'string') { arr.push(v) }
          })
        } else if (csv.length > 0) {
          arr = csv.split(',')
        }
      }
      return arr
    },

    /**
     * Convert market price types array and filter out any suspended price types. A price type filtering will be applied if all other suspension criterias ar matched
     * @param {KillswitchMarket} market market to use to calculate for the rest of the suspension fields; if missing, will return same list of price types
     * @param {KillswitchEvent} event optional event data to know better what kind of event is this
     * @returns {Array}
     */
    ksMarketActivePriceTypes (market, event = null) {
      let mcsvpricetypes = []
      if (typeof market.csvpricetypes !== 'undefined') {
        mcsvpricetypes = this.ksPriceTypeCsvToArray(market.csvpricetypes) // Prematch price types (from generators)
      } else if (typeof market.activepricetypes !== 'undefined') {
        mcsvpricetypes = this.ksPriceTypeCsvToArray(market.activepricetypes) // Live price types (from LiveWire)
      }
      let mactivepricetypes = mcsvpricetypes

      if (this.ksIsKillswitchActive) {
        let mislive = this.ksIsMarketLive(market, event)
        let midfosporttype = market.idfosporttype || ''
        let midfosport = market.idfosport || ''
        let midfomarkettype = (market.idfomarkettype || '') + ''

        let suspensions = this.ksSuspensions
        suspensions.forEach((suspension) => {
          let suspRulesMatchCount = 0
          let isRuleNotMached = false
          if (!isRuleNotMached && ((suspension.is4live === true && mislive === true) || (suspension.is4prematch === true && mislive === false))) { suspRulesMatchCount++ } else { isRuleNotMached = true } // match for Live/PreMatch status
          if (!isRuleNotMached && (suspension.fosporttypes.length < 1 || suspension.fosporttypes.includes(midfosporttype))) { suspRulesMatchCount++ } else { isRuleNotMached = true } // match for IDFO Sport Types
          if (!isRuleNotMached && (suspension.fosports.length < 1 || suspension.fosports.includes(midfosport))) { suspRulesMatchCount++ } else { isRuleNotMached = true } // match for IDFO Sport
          if (!isRuleNotMached && (suspension.fomarkettypes.length < 1 || suspension.fomarkettypes.includes(midfomarkettype))) { suspRulesMatchCount++ } else { isRuleNotMached = true } // match for IDFO Market Types

          if (suspRulesMatchCount >= 4) {
            if (suspension.fopricetypes.length < 1) {
              mactivepricetypes = []
            } else {
              mactivepricetypes = mactivepricetypes.filter(pricetype => !suspension.fopricetypes.includes(pricetype))
            }
          }
        })
      }
      // console.log('ksMarketActivePriceTypes', market, mactivepricetypes)
      return mactivepricetypes
    },

    /**
     * Calculate if event is suspended via suspensions information from Killswitch
     * @param {KillswitchEvent} event
     * @returns {boolean}
     */
    ksIsEventSuspended (event) {
      let isSuspended = false
      if (this.ksIsKillswitchActive) {
        if (typeof event !== 'undefined' && event !== null) {
          let eidfosporttype = event.idfosporttype || ''
          let eidfosport = event.idfosport || ''
          let eislive = false
          if (this.ksLiveEvents.find(ev => ev.idfoevent === event.idfoevent || ev.foeventantepost === event.idfoevent)) { eislive = true }

          let suspensions = this.ksSuspensions
          suspensions.forEach((suspension) => {
            let suspRulesMatchCount = 0
            let isRuleNotMached = false
            if (!isRuleNotMached && (suspension.fomarkettypes.length < 1)) { suspRulesMatchCount++ } else { isRuleNotMached = true } // match for all IDFO Market Types
            if (!isRuleNotMached && (suspension.fopricetypes.length < 1)) { suspRulesMatchCount++ } else { isRuleNotMached = true } // match for all Price Types
            if (!isRuleNotMached && ((suspension.is4live === true && eislive === true) || (suspension.is4prematch === true && eislive === false))) { suspRulesMatchCount++ } else { isRuleNotMached = true } // match for Live/PreMatch status
            if (!isRuleNotMached && (suspension.fosporttypes.length < 1 || suspension.fosporttypes.includes(eidfosporttype))) { suspRulesMatchCount++ } else { isRuleNotMached = true } // match for IDFO Sport Types
            if (!isRuleNotMached && (suspension.fosports.length < 1 || suspension.fosports.includes(eidfosport))) { suspRulesMatchCount++ } else { isRuleNotMached = true } // match for IDFO Sport

            if (suspRulesMatchCount >= 5) { isSuspended = true }
          })
        }
      }
      return isSuspended
    },

    /**
     * Calculate if market is live based on LiveWire data
     * @param {KillswitchMarket} market
     * @param {KillswitchEvent} event
     * @returns {boolean}
     */
    ksIsMarketLive (market, event = null) {
      let isLive = false
      if (typeof market !== 'undefined' && market !== null) {
        let midfoevent = event && event.idfoevent ? event.idfoevent : market.idfoevent
        let midfomarket = market.idfomarket
        let liveEventForMarket = this.ksLiveEvents.find(ev => ev.idfoevent === midfoevent || ev.foeventantepost === midfoevent)
        if (liveEventForMarket && liveEventForMarket.markets) {
          let liveMarket = liveEventForMarket.markets.find(markettmp => markettmp.idfomarket === midfomarket)
          if (liveMarket) { isLive = true }
        }
      }
      return isLive
    },

    /**
     * Calculate if market is suspended via suspensions information from Killswitch
     * @param {KillswitchMarket} market
     * @param {KillswitchEvent} event
     * @returns {boolean}
     */
    ksIsMarketSuspended (market, event = null) {
      let isSuspended = false
      if (this.ksIsKillswitchActive) {
        if (typeof market !== 'undefined' && market !== null) {
          let mislive = this.ksIsMarketLive(market, event)
          let midfosporttype = market.idfosporttype || ''
          let midfosport = market.idfosport || ''
          let midfomarkettype = (market.idfomarkettype || '') + ''
          let mcsvpricetypes = []
          if (typeof market.csvpricetypes !== 'undefined') {
            mcsvpricetypes = this.ksPriceTypeCsvToArray(market.csvpricetypes) // Prematch price types (from generators)
          } else if (typeof market.activepricetypes !== 'undefined') {
            mcsvpricetypes = this.ksPriceTypeCsvToArray(market.activepricetypes) // Live price types (from LiveWire)
          }

          let suspensions = this.ksSuspensions
          suspensions.forEach((suspension) => {
            if (!isSuspended) {
              let suspRulesMatchCount = 0
              let isRuleNotMached = false
              if (!isRuleNotMached && ((suspension.is4live === true && mislive === true) || (suspension.is4prematch === true && mislive === false))) { suspRulesMatchCount++ } else { isRuleNotMached = true } // match for Live/PreMatch status
              if (!isRuleNotMached && (suspension.fosporttypes.length < 1 || suspension.fosporttypes.includes(midfosporttype))) { suspRulesMatchCount++ } else { isRuleNotMached = true } // match for IDFO Sport Types
              if (!isRuleNotMached && (suspension.fosports.length < 1 || suspension.fosports.includes(midfosport))) { suspRulesMatchCount++ } else { isRuleNotMached = true } // match for IDFO Sport
              if (!isRuleNotMached && (suspension.fomarkettypes.length < 1 || suspension.fomarkettypes.includes(midfomarkettype))) { suspRulesMatchCount++ } else { isRuleNotMached = true } // match for IDFO Market Types
              if (!isRuleNotMached && (suspension.fopricetypes.length < 1 || mcsvpricetypes.filter(x => !suspension.fopricetypes.includes(x)).length < 1)) { suspRulesMatchCount++ } else { isRuleNotMached = true } // match for All Price Types

              if (suspRulesMatchCount >= 5) {
                // if (market) { console.log('ksIsMarketSuspended(market,event) suspension match', market, event, suspension) }
                isSuspended = true
              }
            }
          })
        }
        // if (market) { console.log('ksIsMarketSuspended(market,event)', market, event, market.idfomarket, market.externaldescription || market.eventname, market.idfosporttype, market.idfosport, market.idfomarkettype, market.csvpricetypes, isSuspended ? 'suspended' : 'not suspended') }
      }
      return isSuspended
    },

    /**
     * Pre process event object based on Killswitch data
     * @param {KillswitchEvent} event
     * @returns {KillswitchEvent} processed event
     */
    ksPreProcessEven (event) {
      if (this.ksIsKillswitchActive) {
        if (event && this.ksIsEventSuspended(event)) {
          event.istradable = false
          if (event.markets && Array.isArray(event.markets)) {
            for (let m = 0; m < event.markets.length; m++) {
              if (event.markets[m]) {
                event.markets[m].istradable = false
                if (event.markets[m].selections && Array.isArray(event.markets[m].selections)) {
                  for (let s = 0; s < event.markets[m].selections.length; s++) {
                    if (event.markets[m].selections[s]) { event.markets[m].selections[s].istradable = false }
                  }
                }
              }
            }
          }
        }

        if (event && event.markets && Array.isArray(event.markets)) {
          for (let m = 0; m < event.markets.length; m++) {
            if (event.markets[m]) {
              if (event.markets[m].csvpricetypes) {
                event.markets[m].csvpricetypes = this.ksMarketActivePriceTypes(event.markets[m], event).join(',')
              }
            }
          }
        }
      }
      return event
    },
    /**
     * Pre process market object based on Killswitch data
     * @param {KillswitchMarket} market
     * @param {KillswitchEvent} event
     * @returns {KillswitchMarket} processed market
     */
    ksPreProcessMarket (market, event = null) {
      if (this.ksIsKillswitchActive) {
        if (market && event && this.ksIsEventSuspended(event)) { market.istradable = false }
        if (market) {
          if (market.csvpricetypes) { market.csvpricetypes = this.ksMarketActivePriceTypes(market, event).join(',') }
          if (this.ksIsMarketSuspended(market)) {
            market.istradable = false
            if (market.selections && Array.isArray(market.selections)) {
              for (let s = 0; s < market.selections.length; s++) {
                if (market.selections[s]) { market.selections[s].istradable = false }
              }
            }
          }
        }
      }
      return market
    },

    /**
     * Pre process market object based on Killswitch data
     * @param {Array<KillswitchMarket>} markets
     * @param {KillswitchEvent} event
     * @returns {Array<KillswitchMarket>} processed market
     */
    ksPreProcessMarkets (markets, event = null) {
      let markets2 = markets
      if (this.ksIsKillswitchActive) {
        if (markets && Array.isArray(markets)) {
          let markets2 = []
          markets.forEach(market => { markets2.push(this.ksPreProcessMarket(market, event)) })
        }
      }
      return markets2
    },

    /**
     * Pre process selection object based on Killswitch data
     * @param {KillswitchSelection} selection
     * @param {KillswitchMarket} market
     * @param {KillswitchEvent} event
     * @returns {KillswitchSelection} processed market
     */
    ksPreProcessSelection (selection, market = null, event = null) {
      if (this.ksIsKillswitchActive) {
        if (selection && market && this.ksIsMarketSuspended(market, event)) {
          selection.istradable = false
        }
      }
      return selection
    },
    /**
     * Calculate if market is suspended via suspensions information from Killswitch
     * @param {{'selection':{KillswitchSelection}, 'market':{KillswitchMarket}, 'event':{KillswitchEvent}}} seldata object with selection related data, there can be other key/values in the object
     * @returns {Object}
     */
    ksPreProcessSelectionData (seldata) {
      if (this.ksIsKillswitchActive && seldata) {
        if (seldata['selection'] && seldata['market'] && seldata['event']) {
          seldata['selection'] = this.ksPreProcessSelection(seldata['selection'], seldata['market'], seldata['event'])
          seldata['market'] = this.ksPreProcessMarket(seldata['market'], seldata['event'])
          seldata['event'] = this.ksPreProcessEven(seldata['event'])
        } else if (seldata['selection'] && seldata['market']) {
          seldata['selection'] = this.ksPreProcessSelection(seldata['selection'], seldata['market'])
          seldata['market'] = this.ksPreProcessMarket(seldata['market'])
        } else if (seldata['selection']) {
          seldata['selection'] = this.ksPreProcessSelection(seldata['selection'])
        }
      }
      return seldata
    }
  },
  created () {
    // console.log('Kill Switch mixin created!!!')
    this.ksKillswitchInit()
  }
}



// WEBPACK FOOTER //
// ./src/components/mixins/killswitch.js